import { Alert, Checkbox } from "antd"
import { CheckboxChangeEvent } from "antd/lib/checkbox"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { Button } from "components/Button"
import PreviousButton from "components/Previous/PreviousButton"
import { Steps } from "components/Steps"
import { useStepper } from "components/Steps/hooks"
import paymentActions from "containers/Payment/services/actions"
import actionsSurvey from "containers/Survey/services/actions"
import { PATH } from "containers/Survey/models"
import teleexpertiseActions from "containers/Teleexpertise/services/actions"
import { DERMATO_RECAP_PAGE, TELEEXPERTISE_END_PAGE } from "core/constants"
import { cdn } from "core/cdn"
import { useLanguage } from "locales"
import { PaymentStore, SurveyStore, UploadDocStore } from "types/store"

// Duree minimale d'affichage du loader du bouton d'envoi.
const SENDING_MIN_DURATION = 1000

const TeleexpertiseConsent: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { languages } = useLanguage()
  const { current, insideStep, totalInsideSteps, totalSteps } = useStepper()
  const { answers, questions } = useSelector(
    ({ survey }: { survey: SurveyStore }) => survey,
  )
  const dermatoPatientDetails = useSelector(
    ({ upload }: { upload: UploadDocStore }) => upload.dermatoPatientDetails,
  )
  const { loading, success, message } = useSelector(
    ({ payment }: { payment: PaymentStore }) => payment,
  )

  const [pharmacistConsent, setPharmacistConsent] = useState(false)
  const [informationConsent, setInformationConsent] = useState(false)
  const [patientConsent, setPatientConsent] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    dispatch(paymentActions.reset())
  }, [dispatch])

  useEffect(() => {
    if (!sending) return undefined
    const timer = setTimeout(() => setSending(false), SENDING_MIN_DURATION)
    return () => clearTimeout(timer)
  }, [sending])

  useEffect(() => {
    if (submitted && success && !sending) {
      dispatch(actionsSurvey.reset())
      navigate(TELEEXPERTISE_END_PAGE)
    }
  }, [submitted, success, sending, dispatch, navigate])

  const previousStep = () => {
    dispatch(actionsSurvey.jumpTo(PATH.dermato_recap))
    navigate(DERMATO_RECAP_PAGE)
  }

  const submitRequest = () => {
    setSubmitted(true)
    setSending(true)
    dispatch(
      teleexpertiseActions.create(
        answers,
        questions,
        patientConsent,
        pharmacistConsent,
        dermatoPatientDetails,
      ),
    )
  }

  const canSubmit =
    pharmacistConsent && informationConsent && patientConsent && !loading && !sending

  return (
    <>
      <Steps
        current={current}
        insideStep={insideStep}
        totalInsideSteps={totalInsideSteps}
        totalSteps={totalSteps}
      />
      <PreviousButton onClick={previousStep} />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 80, width: "100%", minHeight: "80vh", padding: "80px 0 20px" }}>
        {message && (
          <Alert type={message.type} message={message.text} style={{ width: "min(1000px, 90vw)" }} />
        )}

        {/* Image + Title */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 40 }}>
          <img src={cdn("/images/shield_illustration.svg")} style={{ width: 200, height: 200 }} alt="" />
          <div style={{ fontSize: 48, fontWeight: 500, color: "#211452", textAlign: "center" }}>
            {languages.teleexpertiseConsentTitle}
          </div>
        </div>

        {/* Checkbox rows */}
        <div style={{ display: "flex", flexDirection: "column", gap: 60, width: "min(1000px, 90vw)" }}>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <Checkbox
              checked={pharmacistConsent}
              onChange={(event: CheckboxChangeEvent) => setPharmacistConsent(event.target.checked)}
              style={{ flexShrink: 0, marginTop: 4 }}
            />
            <span style={{ fontSize: 28, color: "#022644", lineHeight: 1.4, cursor: "pointer" }} onClick={() => setPharmacistConsent(!pharmacistConsent)}>
              {languages.teleexpertisePharmacistConsent}
            </span>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <Checkbox
              checked={informationConsent}
              onChange={(event: CheckboxChangeEvent) => setInformationConsent(event.target.checked)}
              style={{ flexShrink: 0, marginTop: 4 }}
            />
            <span style={{ fontSize: 28, color: "#022644", lineHeight: 1.4, cursor: "pointer" }} onClick={() => setInformationConsent(!informationConsent)}>
              {languages.teleexpertiseInformationConsent}
            </span>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <Checkbox
              checked={patientConsent}
              onChange={(event: CheckboxChangeEvent) => setPatientConsent(event.target.checked)}
              style={{ flexShrink: 0, marginTop: 4 }}
            />
            <span style={{ fontSize: 28, color: "#022644", lineHeight: 1.4, cursor: "pointer" }} onClick={() => setPatientConsent(!patientConsent)}>
              {languages.teleexpertisePatientConsent}
            </span>
          </div>
        </div>

        {/* Submit button */}
        <Button
          type="primary"
          onClick={submitRequest}
          disabled={!canSubmit}
          loading={loading || sending}
          style={{ fontSize: 28, padding: "27px 30px", height: "auto", borderRadius: 4 }}
        >
          {languages.teleexpertiseConsentSubmit}
        </Button>
      </div>
    </>
  )
}

export default TeleexpertiseConsent
